import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronsUpDown, Check, Loader2, AlertCircle, RefreshCw,
  Layers, Building2, Search,
} from "lucide-react";
import type { ContextType } from "../../data/appNavConfig";
import { useProject } from "../../contexts/ProjectContext";
import { useReadOnly } from "../../contexts/ReadOnlyContext";
import { useAppShell } from "../../contexts/AppShellContext";

interface SelectorItem {
  id: string;
  name: string;
  isCurrent: boolean;
}

interface BaseSelectorProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  items: SelectorItem[];
  currentName: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
}

function BaseSelector({
  icon: Icon, label, items, currentName, onSelect, isLoading, error, onRetry,
}: BaseSelectorProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const filteredItems = filter
    ? items.filter((i) => i.name.toLowerCase().includes(filter.toLowerCase()))
    : items;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setFilter("");
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        itemRefs.current[(index + 1) % filteredItems.length]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        itemRefs.current[(index - 1 + filteredItems.length) % filteredItems.length]?.focus();
      } else if (e.key === "Tab") {
        setOpen(false);
        setFilter("");
      }
    },
    [filteredItems.length],
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1" role="status" aria-label={`Loading ${label}`}>
        <Loader2 className="size-3.5 animate-spin text-slate-400" />
        <span className="text-[12px] text-slate-400">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1" role="alert">
        <AlertCircle className="size-3.5 text-red-400 shrink-0" />
        <span className="text-[12px] text-red-500 truncate max-w-[160px]">{error}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center justify-center gap-1 px-1.5 py-0.5 text-[11px] text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors min-w-[44px] min-h-[44px]"
            aria-label={`Retry loading ${label}`}
          >
            <RefreshCw className="size-3" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (items.length === 0) return null;

  if (items.length === 1) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200/80 w-full h-8">
        <Icon className="size-4 shrink-0 text-slate-400" />
        <span className="text-[13px] font-medium text-slate-700 truncate flex-1 min-w-0 text-left">
          {currentName || items[0].name}
        </span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
          }
        }}
        className="flex items-center gap-1.5 px-2.5 rounded-md border border-slate-200/80 text-slate-700 hover:border-slate-300 hover:bg-white/60 transition-colors w-full h-8"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Switch ${label}. Current: ${currentName || "None"}`}
      >
        <Icon className="size-4 shrink-0 text-slate-400" />
        <span className="text-[13px] font-medium truncate flex-1 min-w-0 text-left">
          {currentName || `Select ${label}`}
        </span>
        <ChevronsUpDown className="size-3.5 text-slate-300 shrink-0" />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 w-full max-h-72 overflow-hidden"
          role="listbox"
          aria-label={`Available ${label}s`}
        >
          {items.length > 4 && (
            <div className="relative px-2 pt-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 mt-1 size-3.5 text-slate-400" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Filter ${label.toLowerCase()}s…`}
                className="w-full pl-7 pr-2 py-1.5 text-[12px] rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                autoFocus
              />
            </div>
          )}
          <div className="p-1.5 space-y-0.5 max-h-56 overflow-y-auto">
            {filteredItems.map((item, index) => (
              <button
                key={item.id}
                ref={(el) => { itemRefs.current[index] = el; }}
                role="option"
                aria-selected={item.isCurrent}
                onClick={() => {
                  onSelect(item.id);
                  setOpen(false);
                  setFilter("");
                  triggerRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(item.id);
                    setOpen(false);
                    setFilter("");
                    triggerRef.current?.focus();
                  }
                  handleKeyDown(e, index);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[12px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset ${
                  item.isCurrent
                    ? "bg-indigo-50 text-indigo-700 font-medium border-l-2 border-indigo-500 pl-2"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <span className="flex-1 text-left truncate">{item.name}</span>
                {item.isCurrent && <Check className="size-3.5 text-indigo-500 shrink-0" />}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-slate-400 text-center">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectSelector() {
  const { currentProject, projects, switchProject } = useProject();
  const readOnly = useReadOnly();
  const navigate = useNavigate();
  const items: SelectorItem[] = projects.map((p) => ({
    id: p.slug, name: p.name, isCurrent: p.slug === currentProject?.slug,
  }));
  const base = readOnly ? "/docs" : "/studio";
  const handleSelect = useCallback(
    (slug: string) => { switchProject(slug); navigate(`${base}/projects/${slug}`); },
    [switchProject, navigate, base],
  );
  return (
    <BaseSelector
      icon={Layers} label="Project" items={items}
      currentName={currentProject?.name ?? null}
      onSelect={handleSelect} isLoading={false} error={null}
    />
  );
}

function PartnerPicker() {
  const { currentPartner, partners, switchPartner } = useAppShell();
  const navigate = useNavigate();
  const items: SelectorItem[] = partners.map((p) => ({
    id: p.partnerId, name: p.partnerName, isCurrent: p.partnerId === currentPartner?.partnerId,
  }));
  const handleSelect = useCallback(
    (partnerId: string) => { switchPartner(partnerId); navigate("/partners"); },
    [switchPartner, navigate],
  );
  return (
    <BaseSelector
      icon={Building2} label="Partner" items={items}
      currentName={currentPartner?.partnerName ?? null}
      onSelect={handleSelect} isLoading={false} error={null}
    />
  );
}

export interface ContextSelectorProps {
  contextType: ContextType;
}

export default function ContextSelector({ contextType }: ContextSelectorProps) {
  switch (contextType) {
    case "team": return null;
    case "project": return <ProjectSelector />;
    case "partner": return <PartnerPicker />;
    case "none": return null;
  }
}
